import React, { useEffect, useMemo, useState } from 'react';
import { MessageSquare, Send, Search, Filter, Users, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Label } from '../../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { useAuthStore } from '../../../store/authStore';
import { useFeedbacks, useNotifications, useUsers } from '../../../hooks/useDatabase';
import { toast } from 'sonner';

export default function CEOFeedbackPage() {
  const { user } = useAuthStore();
  const { getUsers } = useUsers();
  const { getFeedbacksForUser, createFeedback, loading: feedbackLoading } = useFeedbacks();
  const { createNotification } = useNotifications();

  const [allUsers, setAllUsers] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sendText, setSendText] = useState('');
  const [sendType, setSendType] = useState('general');
  const [sendTarget, setSendTarget] = useState('supervisor');
  const [selectedRecipient, setSelectedRecipient] = useState('');
  const [activeTab, setActiveTab] = useState('received');

  useEffect(() => {
    (async () => {
      const users = await getUsers();
      setAllUsers(users.filter(u => u.status !== 'deleted' && ['supervisor', 'employee', 'admin'].includes(u.role)));
    })();
  }, [getUsers, user]);

  useEffect(() => {
    (async () => {
      const list = await getFeedbacksForUser(user?.id);
      setFeedbacks(list || []);
    })();
  }, [getFeedbacksForUser, user]);

  const supervisors = allUsers.filter(u => u.role === 'supervisor');
  const employees = allUsers.filter(u => u.role === 'employee');
  const admins = allUsers.filter(u => u.role === 'admin');

  const departments = useMemo(() => ['all', ...new Set(allUsers.map(u => u.profile?.department).filter(Boolean))], [allUsers]);

  const recipientList = useMemo(() => {
    if (sendTarget === 'admin') return admins;
    if (sendTarget === 'supervisor') return supervisors;
    return employees;
  }, [sendTarget, admins, supervisors, employees]);

  const filteredRecipients = recipientList.filter(u => {
    const matchesDept = departmentFilter === 'all' || u.profile?.department === departmentFilter;
    const matchesSearch = searchQuery === '' ||
      u.profile?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.profile?.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesDept && matchesSearch;
  });

  const filteredFeedbacks = feedbacks.filter(fb => {
    const matchesType = typeFilter === 'all' || fb.type === typeFilter;
    return matchesType;
  });

  const handleSend = async () => {
    if (!selectedRecipient) {
      toast.error('Please select a recipient');
      return;
    }
    if (!sendText.trim()) {
      toast.error('Feedback message is required');
      return;
    }

    const recipient = allUsers.find(u => u.id === selectedRecipient);
    if (!recipient) {
      toast.error('Recipient not found');
      return;
    }

    try {
      await createFeedback({
        senderId: user?.id,
        senderRole: 'ceo',
        receiverId: recipient.id,
        receiverRole: recipient.role,
        message: sendText.trim(),
        type: sendType,
        department: recipient.profile?.department || ''
      });

      await createNotification({
        userId: recipient.id,
        type: 'feedback_received',
        title: 'Feedback from CEO',
        message: sendText.slice(0, 80),
        link: recipient.role === 'admin' ? '/admin/feedback' 
            : recipient.role === 'supervisor' ? '/supervisor/feedback' 
            : '/employee/feedback'
      });

      toast.success('Feedback sent successfully');
      setSendText('');
      setSendType('general');
      setSelectedRecipient('');
    } catch (error) {
      toast.error('Failed to send feedback');
    }
  };

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case 'appreciation': return 'bg-emerald-100 text-emerald-700';
      case 'issue': return 'bg-rose-100 text-rose-700';
      case 'suggestion': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Feedback</h1>
          <p className="text-gray-500 mt-1">Send feedback to your organization and view messages</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100 p-1">
          <TabsTrigger value="received" className="data-[state=active]:bg-white">Received Feedback</TabsTrigger>
          <TabsTrigger value="send" className="data-[state=active]:bg-white">Send Feedback</TabsTrigger>
        </TabsList>

        <TabsContent value="received" className="space-y-4 mt-6">
          {/* Filter Bar */}
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-4 pb-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-[180px]">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="appreciation">Appreciation</SelectItem>
                    <SelectItem value="issue">Issue</SelectItem>
                    <SelectItem value="suggestion">Suggestion</SelectItem>
                  </SelectContent>
                </Select>
                <Badge variant="secondary" className="h-10 px-4 flex items-center">
                  {filteredFeedbacks.length} feedback{filteredFeedbacks.length !== 1 ? 's' : ''}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Feedback List */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-indigo-500" />
                Feedback Received
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {filteredFeedbacks.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500">No feedback received yet</p>
                  <p className="text-sm text-gray-400 mt-1">Feedback from your organization will appear here</p>
                </div>
              ) : (
                filteredFeedbacks.map(fb => {
                  const sender = allUsers.find(u => u.id === fb.senderId);
                  return (
                    <div key={fb.id} className="p-4 border border-gray-100 rounded-xl hover:border-indigo-200 transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center text-white font-medium text-sm">
                            {fb.anonymous ? '?' : `${sender?.profile?.firstName?.[0] || ''}${sender?.profile?.lastName?.[0] || ''}`}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              {fb.anonymous ? 'Anonymous' : sender ? `${sender.profile?.firstName} ${sender.profile?.lastName}` : 'Unknown'}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <Badge variant="outline" className="text-xs capitalize">{fb.senderRole}</Badge>
                              {fb.department && <span className="text-xs text-gray-500">{fb.department}</span>}
                            </div>
                          </div>
                        </div>
                        <Badge className={getTypeBadgeColor(fb.type)}>{fb.type}</Badge>
                      </div>
                      <p className="text-gray-700 mt-3 whitespace-pre-line">{fb.message}</p>
                      <p className="text-xs text-gray-400 mt-3">{new Date(fb.createdAt).toLocaleString()}</p>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="send" className="space-y-4 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Send Feedback Form */}
            <Card className="border-0 shadow-sm lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Send className="w-5 h-5 text-indigo-500" />
                  Send Feedback
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Send To</Label>
                  <Select value={sendTarget} onValueChange={(v) => { setSendTarget(v); setSelectedRecipient(''); }}>
                    <SelectTrigger><SelectValue placeholder="Choose role" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="supervisor">Supervisor</SelectItem>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Department</Label>
                  <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                    <SelectTrigger><SelectValue placeholder="Department" /></SelectTrigger>
                    <SelectContent>
                      {departments.map(dept => (
                        <SelectItem key={dept} value={dept}>{dept === 'all' ? 'All Departments' : dept}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      className="pl-9"
                      placeholder="Search by name or email"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Select Recipient</Label>
                  <div className="border rounded-lg max-h-48 overflow-y-auto">
                    {filteredRecipients.map(u => (
                      <button
                        key={u.id}
                        onClick={() => setSelectedRecipient(u.id)}
                        className={`w-full text-left px-3 py-2 flex items-center gap-3 border-b last:border-0 hover:bg-gray-50 ${selectedRecipient === u.id ? 'bg-indigo-50' : ''}`}
                      >
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white text-xs font-semibold">
                          {u.profile?.firstName?.[0]}{u.profile?.lastName?.[0]}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{u.profile?.firstName} {u.profile?.lastName}</p>
                          <p className="text-xs text-gray-500">{u.profile?.department} • {u.profile?.position}</p>
                        </div>
                      </button>
                    ))}
                    {filteredRecipients.length === 0 && (
                      <div className="p-3 text-sm text-gray-500 text-center">No matching recipients</div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Feedback Type</Label>
                  <Select value={sendType} onValueChange={setSendType}>
                    <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="appreciation">Appreciation</SelectItem>
                      <SelectItem value="issue">Issue</SelectItem>
                      <SelectItem value="suggestion">Suggestion</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Message</Label>
                  <Textarea
                    value={sendText}
                    onChange={(e) => setSendText(e.target.value)}
                    placeholder="Share your feedback..."
                    className="min-h-28"
                  />
                </div>

                <Button 
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white" 
                  onClick={handleSend} 
                  disabled={feedbackLoading}
                >
                  <Send className="w-4 h-4 mr-2" /> {feedbackLoading ? 'Sending...' : 'Send Feedback'}
                </Button>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <div className="lg:col-span-2 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="border-0 shadow-sm">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                        <Users className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900">{supervisors.length}</p>
                        <p className="text-xs text-gray-500">Supervisors</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-violet-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900">{employees.length}</p>
                        <p className="text-xs text-gray-500">Employees</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center">
                        <MessageSquare className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900">{feedbacks.length}</p>
                        <p className="text-xs text-gray-500">Total Feedback</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Activity */}
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Organization Overview</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Appreciation Messages</p>
                        <p className="text-xs text-gray-500">Positive feedback received</p>
                      </div>
                      <Badge className="bg-emerald-100 text-emerald-700">
                        {feedbacks.filter(f => f.type === 'appreciation').length}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Suggestions</p>
                        <p className="text-xs text-gray-500">Improvement ideas</p>
                      </div>
                      <Badge className="bg-amber-100 text-amber-700">
                        {feedbacks.filter(f => f.type === 'suggestion').length}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gradient-to-r from-rose-50 to-pink-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Issues Reported</p>
                        <p className="text-xs text-gray-500">Problems to address</p>
                      </div>
                      <Badge className="bg-rose-100 text-rose-700">
                        {feedbacks.filter(f => f.type === 'issue').length}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
